import React, { useEffect, useRef, useState } from "react";
import { DEFAULT_PIC, DISAPPEARING_OPTIONS } from "./constants";

// =====================================================
// CHAT HEADER
// The open-conversation top bar: back button (mobile), avatar + presence, an
// encryption indicator, and the 3-dot menu (search, disappearing timer,
// block/unblock). Owns its own menu open/close + click-outside so the
// container doesn't have to track that UI state.
// =====================================================

const disappearingLabel = (seconds) =>
  DISAPPEARING_OPTIONS.find((o) => o.seconds === seconds)?.label || "Custom";

function ChatHeader({
  user,
  online,
  blockInfo,
  blockLoading,
  onToggleBlock,
  onToggleSearch,
  onBack,
  disappearingSeconds,
  onSetDisappearing,
  onVoiceCall,
  onVideoCall,
  onOpenBackup,
  callDisabled,
}) {
  const [showMenu, setShowMenu] = useState(false);
  const [showTimer, setShowTimer] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
        setShowTimer(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const closeMenu = () => {
    setShowMenu(false);
    setShowTimer(false);
  };

  return (
    <div className="chat-header">
      <button className="back-btn" onClick={onBack}>
        ⬅
      </button>

      <img src={user.profilePic || DEFAULT_PIC} alt="" />

      <div className="header-info">
        <h3>{user.name}</h3>
        <span className="status-text">
          {online ? "Online" : "Offline"}
          <span
            className="enc-badge"
            title="Messages in this chat are encrypted"
          >
            🔒 Encrypted
          </span>
          {disappearingSeconds > 0 && (
            <span className="disappearing-flag" title="Disappearing messages on">
              ⏱ {disappearingLabel(disappearingSeconds)}
            </span>
          )}
        </span>
      </div>

      <button
        className="call-header-btn"
        onClick={onVoiceCall}
        disabled={callDisabled}
        title="Voice call"
      >
        📞
      </button>
      <button
        className="call-header-btn"
        onClick={onVideoCall}
        disabled={callDisabled}
        title="Video call"
      >
        🎥
      </button>

      <div className="chat-menu-wrap" ref={menuRef}>
        <button
          className="chat-three-dot-btn"
          onClick={() => setShowMenu((v) => !v)}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        {showMenu && (
          <div className="chat-dropdown-menu">
            <div className="chat-dropdown-arrow"></div>

            <button
              className="chat-dropdown-item"
              onClick={() => {
                onToggleSearch();
                closeMenu();
              }}
            >
              🔍 Search Messages
            </button>

            <button
              className="chat-dropdown-item"
              onClick={() => setShowTimer((v) => !v)}
            >
              ⏱ Disappearing Messages
            </button>

            {showTimer && (
              <div className="disappearing-submenu">
                {DISAPPEARING_OPTIONS.map((opt) => (
                  <button
                    key={opt.seconds}
                    className={`disappearing-option ${
                      disappearingSeconds === opt.seconds ? "active" : ""
                    }`}
                    onClick={() => {
                      onSetDisappearing(opt.seconds);
                      closeMenu();
                    }}
                  >
                    {opt.label}
                    {disappearingSeconds === opt.seconds ? " ✓" : ""}
                  </button>
                ))}
              </div>
            )}

            <button
              className="chat-dropdown-item"
              onClick={() => {
                onOpenBackup();
                closeMenu();
              }}
            >
              💾 Chat Backup
            </button>

            <button
              className="chat-dropdown-item chat-item-block"
              disabled={blockLoading}
              onClick={() => {
                onToggleBlock();
                closeMenu();
              }}
            >
              {blockLoading
                ? "Loading..."
                : blockInfo.iBlocked
                ? "✅ Unblock User"
                : "🚫 Block User"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ChatHeader;
